"use client";

import { useEffect, useRef } from "react";
import { osmAttribution, hideLeafletPrefix } from "@/lib/osm-attribution";
import { localizeHotelCity } from "@/lib/category-display";
import { deTekst } from "@/lib/de-text";

/**
 * Offer-page hotel-location map. Renders the deal's partner hotels as numbered,
 * region-colored pins on an OpenStreetMap (leaflet) map, with a legend grouped
 * by region. Shown under the partner-hotels list so the customer sees at a
 * glance WHERE the hotels lie (Dennis 2026-06-03).
 *
 * Region colouring is tuned for the northern-Italy geography of deal 292
 * (lakes vs Adriatic coast vs Tuscan/Ligurian coast). Hotels outside that
 * bounding box fall back to a single neutral pin colour + a plain "Hotels"
 * legend, so the component degrades gracefully on non-Italy deals.
 */
const REGION = {
  meren: { color: "#2563eb", label: "Meren (Garda, Iseo, Maggiore)" },
  adria: { color: "#f97316", label: "Adriatische kust" },
  west: { color: "#16a34a", label: "Toscaanse & Ligurische kust" },
  other: { color: "#f97316", label: "Hotels" },
};
const REGION_ORDER = ["meren", "adria", "west", "other"];

export function hotelRegion(h) {
  const lat = Number(h?.latitude);
  const lng = Number(h?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "other";
  // Only apply the Italy regions inside a northern-Italy bounding box, so
  // hotels elsewhere (e.g. a German theme-park deal) aren't mislabelled.
  const inNorthItaly = lat >= 43.0 && lat <= 47.2 && lng >= 6.5 && lng <= 14.0;
  if (!inNorthItaly) return "other";
  if (lng > 12.0) return "adria";
  if (lng >= 8.3 && lat > 45.3) return "meren";
  return "west";
}

function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

export default function HotelLocationMap({ hotels, dealCity = null, locale = "nl" }) {
  const mapRef = useRef(null);
  const instRef = useRef(null);

  const withCoords = (hotels || []).filter(
    (h) => Number.isFinite(Number(h?.latitude)) && Number.isFinite(Number(h?.longitude)),
  );
  // Order by region (lakes -> Adriatic -> west -> other) then assign 1..N, so
  // the pin numbers and the legend read top-to-bottom by region.
  const ordered = [...withCoords]
    .sort((a, b) => REGION_ORDER.indexOf(hotelRegion(a)) - REGION_ORDER.indexOf(hotelRegion(b)))
    // Plaatsnaam in de taal van de site: de leverancier stuurt "Cologne",
    // de deal heet Keulen (zelfde plaats = naam van de deal).
    .map((h, i) => ({ ...h, city: localizeHotelCity(h.city, dealCity, locale), _n: i + 1, _region: hotelRegion(h) }));

  const depKey = ordered.map((h) => `${h.latitude},${h.longitude},${h._n}`).join("|");

  useEffect(() => {
    if (!mapRef.current || ordered.length === 0) return;
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !mapRef.current) return;
      if (instRef.current) {
        instRef.current.remove();
        instRef.current = null;
      }
      const map = L.map(mapRef.current, { scrollWheelZoom: false, attributionControl: true });
      instRef.current = map;
      hideLeafletPrefix(map);
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: osmAttribution(locale),
        // OSM needs a Referer even on no-referrer redemption pages.
        // Send only the origin, never voucher paths or query parameters.
        referrerPolicy: "origin",
      }).addTo(map);
      const bounds = [];
      ordered.forEach((h) => {
        const lat = Number(h.latitude);
        const lng = Number(h.longitude);
        const color = REGION[h._region].color;
        const icon = L.divIcon({
          className: "",
          html: `<div style="background:${color};border:2px solid #fff;border-radius:50%;width:26px;height:26px;color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.4)">${h._n}</div>`,
          iconSize: [26, 26],
          iconAnchor: [13, 13],
          popupAnchor: [0, -13],
        });
        L.marker([lat, lng], { icon })
          .addTo(map)
          .bindPopup(`<b>${escapeHtml(h.hotel_name)}</b><br>${escapeHtml(h.city)}`);
        bounds.push([lat, lng]);
      });
      if (bounds.length === 1) {
        // 13 matches booking/HotelMap.js's single-hotel zoom, for the same
        // reason as the fitBounds cap below: a lone hotel should be framed
        // tightly, not left at the same metro-wide view the multi-hotel
        // case used to force (Dennis 2026-08-12 follow-up).
        map.setView(bounds[0], 13);
      } else {
        // maxZoom must stay generous: this component is shared by every
        // offer/voucher page, not just region-wide deals like 292. A low cap
        // (previously 9, tuned for the northern-Italy spread) also clamps
        // tightly-clustered single-city deals (e.g. Berlin, all 8 hotels a
        // few km apart), forcing the map to zoom OUT to the whole
        // metro area instead of framing the hotels (Dennis 2026-08-12).
        // 14 matches the same fitBounds cap already proven for this exact
        // problem in the booking-step map (components/booking/HotelMap.js).
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
      }
    })();
    return () => {
      cancelled = true;
      if (instRef.current) {
        instRef.current.remove();
        instRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  if (ordered.length === 0) return null;

  const groups = {};
  ordered.forEach((h) => {
    (groups[h._region] = groups[h._region] || []).push(h);
  });
  const presentRegions = REGION_ORDER.filter((r) => groups[r]);

  return (
    <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
      <h3 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900">
        <span aria-hidden>📍</span> {deTekst(locale, "sx.hotelmap.title", "Waar liggen de hotels?")}
      </h3>
      <p className="mb-4 text-sm text-gray-500">
        {deTekst(locale, "sx.hotelmap.intro", "Alle deelnemende hotels in één oogopslag. Kies zelf je favoriet.")}
      </p>
      <div className="grid gap-4 md:grid-cols-[1fr_280px]">
        <div ref={mapRef} className="isolate h-[340px] w-full overflow-hidden rounded-xl sm:h-[460px]" />
        <div className="text-sm">
          {presentRegions.map((r) => (
            <div key={r} className="mb-3 last:mb-0">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                {deTekst(locale, `sx.hotelmap.region_${r}`, REGION[r].label)}
              </div>
              {groups[r].map((h) => (
                <div key={h._n} className="flex items-start gap-2 py-1">
                  <span
                    className="mt-0.5 flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full text-[12px] font-bold text-white"
                    style={{ background: REGION[r].color }}
                  >
                    {h._n}
                  </span>
                  <span>
                    <span className="font-semibold text-gray-800">{h.hotel_name}</span>
                    <br />
                    <span className="text-gray-500">{h.city}</span>
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
